import React from 'react';
import { useState } from 'react';
import { FileNode } from './DataRoom';
import { getFolderStructure } from '../../store/dataRoom/apiHelper';
import {
    downloadFile,
    downloadFileUsingURLHelper,
    getPresignedUrl,
} from '../Company/UploadJourney/components/UploadCard/utils/api/helpers';
import { toast } from 'react-toastify';
import { getTimeStamp } from '../../utils/dateUtils';
import { Mixpanel } from '../../utils/mixpanel';
import { useAppSelector } from '../../app/hooks';
import {
    AICA_DATA_ROOM_FILE_DOWNLOADED,
    AICA_DATA_ROOM_FILE_VIEWED,
    AICA_DATA_ROOM_NEW_FILE_DOWNLOADED,
    AICA_DATAROOM_FILE_MOVE,
    AICA_DATAROOM_FILE_DELETE,
    AICA_DATAROOM_FILE_ARCHIVE,
} from '../../utils/constants/mixpanelEvents/investorEvents';
import { getObjAndFileNameS3 } from '../../utils/utils';
import { deleteDoc, unarchiveDoc } from '../../common/MagicUpload/MagicUploadHelper';
import { useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { getSubdomain } from '../../utils/utils';
import { FILE_FOLDER_TYPES } from '../../store/investor/constants';

interface MoveToActiveProps {
    docId: number | string;
    masterFolderName: string;
    fetchFiles?: Function;
    setShowOtherOptions: (show: boolean) => void;
    user: any;
    selectedCompany: any;
    handleUnarchiveFileBulk: (filesWithDocType: Array<{ docId: number | string; docType: string }>) => Promise<boolean>;
}

interface DeleteButtonProps {
    docId: number | string;
    name: string;
    masterFolderName: string;
    fetchFiles?: Function;
    setShowOtherOptions: (show: boolean) => void;
    user: any;
    selectedCompany: any;
    handleDeleteFile: (docIds: (number | string)[], name: string, masterFolderName: string, isArchive: boolean) => Promise<boolean>;
    isArchive: boolean;
}

interface MoveButtonProps {
    docId: number | string;
    masterFolderName: string;
    setShowOtherOptions: (show: boolean) => void;
    setShowDataRoomModal: (modalData: { showModal: boolean; docId: number | string; currentDocType: string }) => void;
    user: any;
    selectedCompany: any;
}

interface ArchiveButtonProps {
    docId: number | string;
    name: string;
    masterFolderName: string;
    fetchFiles?: Function;
    setShowOtherOptions: (show: boolean) => void;
    user: any;
    selectedCompany: any;
    handleDeleteFile: (docIds: (number | string)[], name: string, masterFolderName: string, isArchive: boolean) => Promise<boolean>;
}

interface DeleteFolderButtonProps {
    innerFiles: any[];
    name: string;
    foldertoDelete: string;
    parentFolderName?: string;
    fetchFiles?: Function;
    setShowOtherOptions: (show: boolean) => void;
    user: any;
    selectedCompany: any;
    handleDeleteFile: (docIds: (number | string)[], name: string, masterFolderName: string, isArchive: boolean) => Promise<boolean>;
}

interface ArchiveFolderButtonProps {
    innerFiles: any[];
    name: string;
    foldertoDelete: string;
    parentFolderName?: string;
    fetchFiles?: Function;
    setShowOtherOptions: (show: boolean) => void;
    user: any;
    selectedCompany: any;
    handleDeleteFile: (docIds: (number | string)[], name: string, masterFolderName: string, isArchive: boolean) => Promise<boolean>;
}

const MoveToActive: React.FC<MoveToActiveProps> = ({
    docId,
    masterFolderName,
    fetchFiles,
    setShowOtherOptions,
    user,
    selectedCompany,
    handleUnarchiveFileBulk,
}) => {
    return React.createElement(
        'div',
        {
            onClick: async (e: React.MouseEvent<HTMLDivElement>) => {
                e.stopPropagation();
                if (docId) {
                    await handleUnarchiveFileBulk([{ docId, docType: masterFolderName }]);
                }
                if (fetchFiles) fetchFiles();
                setShowOtherOptions(false);

                Mixpanel.track(AICA_DATAROOM_FILE_MOVE, {
                    userRole: user?.accountType,
                    pnoPlatform: getSubdomain(),
                    userId: user?.userId,
                    currentUrl: window.location.href,
                    organizationId: selectedCompany?.assesseeOrgId,
                });
            },
        },
        'Move to Active',
    );
};

const DeleteButton: React.FC<DeleteButtonProps> = ({
    docId,
    name,
    masterFolderName,
    fetchFiles,
    setShowOtherOptions,
    user,
    selectedCompany,
    handleDeleteFile,
    isArchive,
}) => {
    return React.createElement(
        'div',
        {
            style: { color: '#DF666A' },
            onClick: async (e: React.MouseEvent<HTMLDivElement>) => {
                e.stopPropagation();
                if (docId) await handleDeleteFile([docId], name, masterFolderName, isArchive);
                if (fetchFiles) fetchFiles();
                setShowOtherOptions(false);

                Mixpanel.track(AICA_DATAROOM_FILE_DELETE, {
                    userRole: user?.accountType,
                    pnoPlatform: getSubdomain(),
                    userId: user?.userId,
                    currentUrl: window.location.href,
                    organizationId: selectedCompany?.assesseeOrgId,
                    deletedItemType: FILE_FOLDER_TYPES.FILE,
                });
            },
        },
        'Delete',
    );
};

const MoveButton: React.FC<MoveButtonProps> = ({
    docId,
    masterFolderName,
    setShowOtherOptions,
    setShowDataRoomModal,
    user,
    selectedCompany,
}) => {
    return React.createElement(
        'div',
        {
            onClick: (e: React.MouseEvent<HTMLDivElement>) => {
                e.stopPropagation();
                setShowDataRoomModal({
                    showModal: true,
                    docId,
                    currentDocType: masterFolderName,
                });
                setShowOtherOptions(false);

                Mixpanel.track(AICA_DATAROOM_FILE_MOVE, {
                    userRole: user?.accountType,
                    pnoPlatform: getSubdomain(),
                    userId: user?.userId,
                    currentUrl: window.location.href,
                    organizationId: selectedCompany?.assesseeOrgId,
                });
            },
        },
        'Move',
    );
};

const ArchiveButton: React.FC<ArchiveButtonProps> = ({
    docId,
    name,
    masterFolderName,
    fetchFiles,
    setShowOtherOptions,
    user,
    selectedCompany,
    handleDeleteFile,
}) => {
    return React.createElement(
        'div',
        {
            style: { color: '#DF666A' },
            onClick: async (e: React.MouseEvent<HTMLDivElement>) => {
                e.stopPropagation();
                if (docId) await handleDeleteFile([docId], name, masterFolderName, true);
                if (fetchFiles) fetchFiles();
                setShowOtherOptions(false);

                Mixpanel.track(AICA_DATAROOM_FILE_ARCHIVE, {
                    userRole: user?.accountType,
                    pnoPlatform: getSubdomain(),
                    userId: user?.userId,
                    currentUrl: window.location.href,
                    organizationId: selectedCompany?.assesseeOrgId,
                    archivedItemType: FILE_FOLDER_TYPES.FILE,
                });
            },
        },
        'Archive',
    );
};

// Recursive function to extract all docIds from nested innerFiles
const extractDocIds = (files: any[]): string[] => {
    let docIds: string[] = [];
    files.forEach((file) => {
        if (file.docId) {
            docIds.push(String(file.docId)); // Convert docId to string
        }
        if (file.innerFiles && file.innerFiles.length > 0) {
            docIds = [...docIds, ...extractDocIds(file.innerFiles)]; // Recursively get docIds
        }
    });
    return docIds;
};

const DeleteFolderButton: React.FC<DeleteFolderButtonProps> = ({
    innerFiles,
    name,
    foldertoDelete,
    parentFolderName,
    fetchFiles,
    setShowOtherOptions,
    user,
    selectedCompany,
    handleDeleteFile,
}) => {
    return React.createElement(
        'div',
        {
            style: { color: '#DF666A' },
            onClick: async (e: React.MouseEvent<HTMLDivElement>) => {
                e.stopPropagation();
                if (innerFiles && innerFiles.length > 0) {
                    const docIds = extractDocIds(innerFiles);
                    await handleDeleteFile(docIds, name, foldertoDelete, false);
                    if (fetchFiles) fetchFiles();
                    setShowOtherOptions(false);

                    Mixpanel.track(AICA_DATAROOM_FILE_DELETE, {
                        userRole: user?.accountType,
                        pnoPlatform: getSubdomain(),
                        userId: user?.userId,
                        currentUrl: window.location.href,
                        organizationId: selectedCompany?.assesseeOrgId,
                        deletedItemType: parentFolderName ? FILE_FOLDER_TYPES.SUB_FOLDER : FILE_FOLDER_TYPES.FOLDER,
                    });
                }
            },
        },
        'Delete',
    );
};

const ArchiveFolderButton: React.FC<ArchiveFolderButtonProps> = ({
    innerFiles,
    name,
    foldertoDelete,
    parentFolderName,
    fetchFiles,
    setShowOtherOptions,
    user,
    selectedCompany,
    handleDeleteFile,
}) => {
    return React.createElement(
        'div',
        {
            style: { color: '#DF666A' },
            onClick: async (e: React.MouseEvent<HTMLDivElement>) => {
                e.stopPropagation();
                if (innerFiles && innerFiles.length > 0) {
                    const docIds = extractDocIds(innerFiles);
                    await handleDeleteFile(docIds, name, foldertoDelete, true);
                    if (fetchFiles) fetchFiles();
                    setShowOtherOptions(false);

                    Mixpanel.track(AICA_DATAROOM_FILE_ARCHIVE, {
                        userRole: user?.accountType,
                        pnoPlatform: getSubdomain(),
                        userId: user?.userId,
                        currentUrl: window.location.href,
                        organizationId: selectedCompany?.assesseeOrgId,
                        archivedItemType: parentFolderName ? FILE_FOLDER_TYPES.SUB_FOLDER : FILE_FOLDER_TYPES.FOLDER,
                    });
                }
            },
        },
        'Archive',
    );
};

// Utility functions for file operations
export const getSelectedFileNodes = (data: any[], selectedItems: Set<string>): any[] => {
    const selectedNodes: any[] = [];
    const findNodes = (nodes: any[]) => {
        nodes.forEach((node) => {
            const nodeId = node.docId ? String(node.docId) : node.name;
            if (selectedItems.has(nodeId)) {
                selectedNodes.push(node);
            }
            if (node.innerFiles) {
                findNodes(node.innerFiles);
            }
        });
    };
    findNodes(data);
    return selectedNodes;
};

export const buildDownloadStructure = (nodes: any[], selectedItems: Set<string>): any[] => {
    const result: any[] = [];

    nodes.forEach((node) => {
        if (node.type === 'folder') {
            // Recursively process folder's inner files
            const filteredInnerFiles = buildDownloadStructure(node.innerFiles || [], selectedItems);

            // Include folder if it has any selected files (even deeply nested)
            if (filteredInnerFiles.length > 0) {
                result.push({
                    ...node,
                    innerFiles: filteredInnerFiles,
                });
            }
        } else if (node.type === 'file') {
            // Include file if it's selected
            const fileId = node.docId ? String(node.docId) : node.name;
            if (selectedItems.has(fileId)) {
                result.push({
                    ...node,
                    innerFiles: [], // Files don't have innerFiles, ensure empty array
                });
            }
        }
    });

    return result;
};

export const getAllFileIds = (data: any[]): string[] => {
    const ids: string[] = [];
    const collectFileIds = (nodes: any[]) => {
        nodes.forEach((node) => {
            if (node.type === 'file' && node.docId) {
                ids.push(String(node.docId));
            }
            if (node.innerFiles) {
                collectFileIds(node.innerFiles);
            }
        });
    };
    collectFileIds(data);
    return ids;
};

export const getAllFolderIds = (data: any[]): string[] => {
    const ids: string[] = [];
    const collectFolderIds = (nodes: any[]) => {
        nodes.forEach((node) => {
            if (node.type === 'folder') {
                ids.push(node.name);
            }
            if (node.innerFiles) {
                collectFolderIds(node.innerFiles);
            }
        });
    };
    collectFolderIds(data);
    return ids;
};

export const findMasterFolderForNode = (data: any[], nodeId: string): string => {
    // Helper function to search recursively
    const searchInTree = (nodes: any[], parentFolder: string = ''): string | null => {
        for (const node of nodes) {
            const currentNodeId = node.docId ? String(node.docId) : node.name;

            // If this is a top-level folder (no parent), use its name as master folder
            const currentMasterFolder = node.type === 'folder' && !parentFolder ? node.name : parentFolder;

            // If we found the target node, return its master folder
            if (currentNodeId === nodeId) {
                return currentMasterFolder || node.name;
            }

            // Search in nested files
            if (node.innerFiles && node.innerFiles.length > 0) {
                const found = searchInTree(node.innerFiles, currentMasterFolder);
                if (found) return found;
            }
        }
        return null;
    };

    return searchInTree(data) || 'Data';
};

const useFetchDataRoomFiles = (fetchFiles?: Function) => {
    const [showFile, setShowFile] = useState<boolean>(false);
    const [downloading, setDownloading] = useState<boolean>(false);
    const [downloadingFile, setDownloadingFile] = useState<boolean>(false);
    const [downloadingNew, setDownloadingNew] = useState<boolean>(false);
    const [fileUrl, setFileUrl] = useState<string>('');
    const user = useAppSelector((state) => state.user.userData);
    const companyName = useAppSelector((state) => state.investor.selectedCompany)?.orgName;
    const userEmail = user.primaryEmail;
    const userName = user.userName;
    const params = useParams();

    const handleFileClick = async (docId: number | string, name: string) => {
        if (!isNaN(Number(docId))) {
            const res = await getPresignedUrl({ docId: Number(docId) }, false);
            if (res) {
                Mixpanel.track(AICA_DATA_ROOM_FILE_VIEWED, {
                    userEmail,
                    companyName,
                    userName,
                    fileType: res.fileType,
                });
                if (res.fileType === 'application/pdf') {
                    setShowFile(true);
                    setFileUrl(res.uri);
                } else {
                    await downloadFile(docId, () => {}, name);
                }
            } else {
                toast.error('Error in fetching file');
            }
        } else {
            const { obj, extention }: any = getObjAndFileNameS3(docId);
            const bucketName = obj?.bucket;
            const res = await downloadFileUsingURLHelper(obj.url, bucketName);
            if (res) {
                Mixpanel.track(AICA_DATA_ROOM_FILE_VIEWED, {
                    userEmail,
                    companyName,
                    userName,
                    fileType: extention,
                });
                const fileRes = await fetch(res);
                if (fileRes.status === 200) {
                    const blob = await fileRes.blob();
                    const link = document.createElement('a');
                    link.href = window.URL.createObjectURL(blob);
                    link.download = name ?? 'file';
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                } else {
                    toast.error('Error in downloading file');
                }
            }
        }
    };

    const downloadTheFile = async (docId: number | string, name: string) => {
        setDownloadingFile(true);
        if (!isNaN(Number(docId))) {
            await downloadFile(docId, () => {}, name);
            fetchFiles && fetchFiles();
            setDownloadingFile(false);
        } else {
            const { obj }: any = getObjAndFileNameS3(docId);
            const bucketName = obj?.bucket;

            const res = await downloadFileUsingURLHelper(obj.url, bucketName);
            if (res) {
                const fileRes = await fetch(res);
                if (fileRes.status === 200) {
                    const blob = await fileRes.blob();
                    const link = document.createElement('a');
                    link.href = window.URL.createObjectURL(blob);
                    link.download = name ?? 'file';
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                } else {
                    toast.error('Error in downloading file');
                }
            }
            setDownloadingFile(false);
        }
    };
    const downloadTheFolder = async (data: FileNode[], name: string, isForNewDownload: boolean) => {
        isForNewDownload ? setDownloadingNew(true) : setDownloading(true);
        const payload = {
            parentPath: name,
            requestId: getTimeStamp(),
            fileNodes: data,
            isForNewDownload,
            downloadByOrgId: localStorage.getItem('user_org_id'),
        };
        Mixpanel.track(isForNewDownload ? AICA_DATA_ROOM_NEW_FILE_DOWNLOADED : AICA_DATA_ROOM_FILE_DOWNLOADED, {
            userEmail,
            companyName,
            userName,
        });
        const res = await getFolderStructure(payload);
        if (res) {
            let blob = new Blob([res], { type: 'octet/stream' });
            let fileName = `${name}.zip`;
            let link = document.createElement('a');
            link.href = window.URL.createObjectURL(blob);
            link.download = fileName;
            link.click();
            toast.success('Folder Downloaded Successfully');
            isForNewDownload ? setDownloadingNew(false) : setDownloading(false);
        } else {
            toast.error('Error in fetching folder');
            isForNewDownload ? setDownloadingNew(false) : setDownloading(false);
        }
        fetchFiles && fetchFiles();
    };

    const handleDeleteFile = async (
        docIds: (number | string)[], // Ensure this can accept both number and string types
        name: string,
        masterFolderName: string,
        isArchive: boolean = false,
    ) => {
        let result = false;

        const payload = {
            docIds: docIds.map(String), // Ensure all docIds are strings
            requestId: getTimeStamp(), // Assuming getTimeStamp() returns a valid timestamp string
            orgId: params.id, // Your orgId from params
            pnoId: user.investorOrgId, // Your pnoId from user
            docType: masterFolderName, // The type of document you're working with
            archive: isArchive, // boolean indicating whether to archive or delete
        };

        // Call deleteDoc with the constructed payload
        await deleteDoc(payload, {
            onSuccess: () => {
                result = true;
                isArchive ? toast.success('Archived successfully') : toast.success('Deleted successfully');
                if (fetchFiles) fetchFiles();
            },
        });

        return result;
    };

    const handleArchiveFileBulk = async (filesWithDocType: Array<{ docId: number | string; docType: string }>) => {
        let result = false;

        const payload = {
            deleteItems: filesWithDocType.map((file) => ({
                docId: String(file.docId),
                docType: file.docType,
            })),
            requestId: getTimeStamp(),
            orgId: params.id,
            pnoId: user.investorOrgId,
            archive: true,
        };

        await deleteDoc(payload, {
            onSuccess: () => {
                result = true;
                toast.success('Archived successfully');
                if (fetchFiles) fetchFiles();
            },
        });

        return result;
    };

    const handleUnarchiveFileBulk = async (filesWithDocType: Array<{ docId: number | string; docType: string }>) => {
        let result = false;

        const payload = {
            deleteItems: filesWithDocType.map((file) => ({
                docId: String(file.docId),
                docType: file.docType,
            })),
            requestId: getTimeStamp(),
            orgId: params.id,
            pnoId: user.investorOrgId,
        };

        await unarchiveDoc(payload, {
            onSuccess: (message?: string) => {
                result = true;
                toast.success(message || 'Moved to active successfully');
                if (fetchFiles) fetchFiles();
            },
        });

        return result;
    };

    const getFormattedDate = (source: string, date: string) => {
        if (source === 'folder' || !date) return '';

        const formats = ['DD-MM-YYYY', 'DD/MM/YYYY', 'YYYY-MM-DD'];
        const parsed = dayjs(date, formats, true);
        if (parsed.isValid()) return parsed.format('DD MMM YYYY');

        console.warn('Unrecognized date format:', date);
        return '';
    };

    return {
        showFile,
        handleFileClick,
        setShowFile,
        fileUrl,
        downloadTheFile,
        downloadTheFolder,
        downloading,
        handleDeleteFile,
        handleArchiveFileBulk,
        handleUnarchiveFileBulk,
        downloadingNew,
        getFormattedDate,
        downloadingFile,
        MoveToActive,
        DeleteButton,
        MoveButton,
        ArchiveButton,
        DeleteFolderButton,
        ArchiveFolderButton,
    };
};

export default useFetchDataRoomFiles;
